import { useState, useEffect, useCallback, useMemo } from 'react';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';

export type ModalMode = 'EDIT' | 'VIEW';

// Optimized deep comparison function that's faster than JSON.stringify
const deepEqual = <T>(obj1: T, obj2: T): boolean => {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null) return obj1 === obj2;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object')
    return obj1 === obj2;

  const keys1 = Object.keys(obj1 as object);
  const keys2 = Object.keys(obj2 as object);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (
      !deepEqual(
        (obj1 as Record<string, unknown>)[key],
        (obj2 as Record<string, unknown>)[key]
      )
    )
      return false;
  }

  return true;
};

export interface UseEditableModalProps<T> {
  entity: T;
  initialMode?: ModalMode;
  onSave: (editedEntity: T) => void | Promise<void>;
  onClose: () => void;
  alwaysDisabledFields?: string[];
  styles: Record<string, React.CSSProperties>; // Modal styles object
}

export interface UseEditableModalReturn<T> {
  // State
  originalEntity: T;
  editedEntity: T;
  setEditedEntity: (entity: T) => void;
  modalMode: ModalMode;
  setModalMode: (mode: ModalMode) => void;
  focusedField: string | null;
  setFocusedField: (field: string | null) => void;

  // Computed values
  hasUnsavedChanges: boolean;
  isSaving: boolean;

  // Actions
  handleSave: () => void;
  handleCancel: () => void;
  handleClose: () => void;
  handleEdit: () => void;

  // Field helpers
  isFieldDisabled: (fieldName: string) => boolean;
  getFloatingLabelStyle: (fieldName: string, hasValue: boolean) => object;
  getFloatingInputStyle: (isTextarea: boolean, fieldName: string) => object;
  getFloatingSelectStyle: (fieldName: string) => object;

  // Header action text — "Edit" in VIEW mode, "Save" in EDIT mode
  getPrimaryActionText: () => string;
}

const useEditableModal = <T>({
  entity,
  initialMode = 'VIEW',
  onSave,
  onClose,
  alwaysDisabledFields = [],
  styles,
}: UseEditableModalProps<T>): UseEditableModalReturn<T> => {
  // State management
  const [originalEntity] = useState<T>(entity);
  const [editedEntity, setEditedEntity] = useState<T>(entity);
  const [modalMode, setModalMode] = useState<ModalMode>(initialMode);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Check if entity has been modified - memoized for performance
  const hasUnsavedChanges = useMemo((): boolean => {
    return !deepEqual(editedEntity, originalEntity);
  }, [editedEntity, originalEntity]);

  // Field state helpers
  const isFieldDisabled = useCallback(
    (fieldName: string): boolean => {
      if (modalMode === 'VIEW') return true;
      return alwaysDisabledFields.includes(fieldName);
    },
    [modalMode, alwaysDisabledFields]
  );

  // Style helpers
  const getFloatingLabelStyle = useCallback(
    (fieldName: string, hasValue: boolean) => {
      if (!styles) return {};

      const isDisabled = isFieldDisabled(fieldName);
      const isFocused = focusedField === fieldName;

      return {
        ...styles.floatingLabelStyle,
        ...(isFocused || hasValue ? styles.floatingLabelFocusedStyle : {}),
        ...(isDisabled ? styles.floatingLabelDisabledStyle : {}),
      };
    },
    [styles, focusedField, isFieldDisabled]
  );

  const getFloatingInputStyle = useCallback(
    (isTextarea: boolean, fieldName: string) => {
      if (!styles) return {};

      const isDisabled = isFieldDisabled(fieldName);
      const isFocused = focusedField === fieldName;

      return {
        ...(isTextarea
          ? styles.floatingTextareaStyle
          : styles.floatingInputStyle),
        ...(isFocused && !isDisabled ? styles.floatingInputFocusedStyle : {}),
        ...(isDisabled ? styles.floatingInputDisabledStyle : {}),
      };
    },
    [styles, focusedField, isFieldDisabled]
  );

  const getFloatingSelectStyle = useCallback(
    (fieldName: string) => {
      if (!styles) return {};

      const isDisabled = isFieldDisabled(fieldName);
      const isFocused = focusedField === fieldName;

      return {
        ...styles.floatingSelectStyle,
        ...(isFocused && !isDisabled ? styles.floatingInputFocusedStyle : {}),
        ...(isDisabled ? styles.floatingSelectDisabledStyle : {}),
      };
    },
    [styles, focusedField, isFieldDisabled]
  );

  // Header action text — "Edit" in VIEW mode, "Save" in EDIT mode
  const getPrimaryActionText = useCallback(() => {
    return modalMode === 'VIEW' ? 'Edit' : 'Save';
  }, [modalMode]);

  // Action handlers
  const handleEdit = useCallback(() => {
    setModalMode('EDIT');
  }, []);

  const handleSave = useCallback(async () => {
    if (modalMode === 'VIEW') {
      handleEdit();
      return;
    }

    if (!hasUnsavedChanges) {
      return; // No changes to save
    }

    setIsSaving(true);
    try {
      await onSave(editedEntity);
    } catch (error) {
      console.error('❌ Modal save operation failed:', error);
      // Let the parent component handle user feedback
      // The error is re-thrown so parent handlers can catch it
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [modalMode, hasUnsavedChanges, editedEntity, onSave, handleEdit]);

  const handleCancel = useCallback(() => {
    if (modalMode === 'EDIT' && hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes. Are you sure you want to discard them?'
      );
      if (!confirmDiscard) {
        return;
      }
    }

    if (modalMode === 'EDIT' && initialMode === 'VIEW') {
      // Reset to original entity and switch to view mode
      setEditedEntity(originalEntity);
      setModalMode('VIEW');
    } else {
      // In view mode, just close
      onClose();
    }
  }, [modalMode, hasUnsavedChanges, originalEntity, onClose, initialMode]);

  const handleClose = useCallback(() => {
    handleCancel(); // Use the same logic as cancel
  }, [handleCancel]);

  // Back-button dismiss: this hook only runs while the modal is mounted, so
  // `isOpen` is always true for its lifetime -- mount/unmount is the signal.
  // `onClose` is `handleCancel`, not a raw always-closes callback, so a back
  // press gets the exact same unsaved-changes confirm and edit->view-revert
  // behavior as clicking Cancel (see useModalBackDismiss's isOpen
  // self-correction, which tolerates a close-handler that doesn't always
  // actually close).
  useModalBackDismiss({ isOpen: true, onClose: handleCancel });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // ESC key to cancel/close
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }

      // Ctrl+Enter (or Cmd+Enter on Mac) to save
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleCancel, handleSave]);

  return {
    // State
    originalEntity,
    editedEntity,
    setEditedEntity,
    modalMode,
    setModalMode,
    focusedField,
    setFocusedField,

    // Computed values
    hasUnsavedChanges,
    isSaving,

    // Actions
    handleSave,
    handleCancel,
    handleClose,
    handleEdit,

    // Field helpers
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getFloatingSelectStyle,

    // Header action text
    getPrimaryActionText,
  };
};

export default useEditableModal;
